const math = require('mathjs');
const _ = require('lodash');
const { degreesToRadians } = require('./degrees');
const { resolutionOf, xyToPolar, polarToXY } = require('./height-map-helpers');

const heapAngleOfRepose = 25; /* degrees */
const funnelAngleOfRepose = 40; /* degrees */
const gamma = 0.6;
const radialScaleFactor = 1;

module.exports = function extrapolateFeedHeightMap(heightMap, binGeometry) {
  const resolution = resolutionOf(heightMap);
  const { xyPointList, valuesList } = _flatPointLists(resolution, heightMap);

  if (xyPointList.length === 0) {
    // No data, nothing to extrapolate from
    return;
  }

  const pointList = xyPointList.map(pt => xyToPolar(pt, resolution));
  const distanceFn = (point1, point2) => {
    // eslint-disable-next-line prefer-const
    let [deltaR, deltaTheta] = math.subtract(point1, point2);
    if (math.abs(deltaTheta) > Math.PI) {
      deltaTheta = math.abs(deltaTheta) - 2 * Math.PI;
    }
    return math.norm([radialScaleFactor * deltaTheta, deltaR]);
  };
  const basisFn = (point1, point2) => Math.exp(-gamma * distanceFn(point1, point2));
  const biasFn = _makeBiasFunction(heightMap, binGeometry);
  const hypothesisFn = _makeHypothesisFn(pointList, valuesList, biasFn, basisFn);

  for (let x = 0; x < resolution; x++) {
    for (let y = 0; y < resolution; y++) {
      if (binGeometry.isHeightMapCoordinateInBin(x, y, resolution) && heightMap[x][y] === null) {
        heightMap[x][y] = hypothesisFn(xyToPolar([x, y], resolution));
      }
    }
  }
};

function _flatPointLists(resolution, heightMap) {
  const xyPointList = [];
  const valuesList = [];
  for (let x = 0; x < resolution; x++) {
    for (let y = 0; y < resolution; y++) {
      if (heightMap[x][y] !== null) {
        xyPointList.push([x, y]);
        valuesList.push(heightMap[x][y]);
      }
    }
  }
  return { xyPointList, valuesList };
}

function _makePhiMatrix(pointsList, basisFn) {
  const phiMatrixRows = [];
  for (let i = 0; i < pointsList.length; i++) {
    const row = [];
    for (let j = 0; j < pointsList.length; j++) {
      row.push(basisFn(pointsList[i], pointsList[j]));
    }
    phiMatrixRows.push(row);
  }
  return math.matrix(phiMatrixRows);
}

function _makeTargetVector(valuesList, pointsList, biasFn) {
  const targetValues = [];
  for (let i = 0; i < valuesList.length; i++) {
    const point = pointsList[i];
    targetValues.push(valuesList[i] - biasFn(point));
  }
  return math.matrix(targetValues);
}

function _makeHypothesisFn(pointsList, valuesList, biasFn, basisFn) {
  const numPoints = pointsList.length;
  const targetVector = _makeTargetVector(valuesList, pointsList, biasFn);
  const phiMatrix = _makePhiMatrix(pointsList, basisFn);
  const weightVector = math.lusolve(phiMatrix, targetVector);
  return (point) => {
    let sum = 0;
    for (let i = 0; i < numPoints; i++) {
      sum += weightVector.subset(math.index(i, 0)) * basisFn(pointsList[i], point);
    }

    return sum + biasFn(point);
  };
}

function _biasTowardsAFeedPileOrCone(heightMap, resolution, binGeometry) {
  // We're going to look at the average height of the feed in:
  //   - the outer ring
  //   - the inner ring
  // Then we'll pick a shape for the bias function by picking:
  //   - an angle
  //   - a single radial point (r, height)
  // to form either  a cone, a funnel, or a flat bias function:
  const outerRingHeightAverage = _averageHeightBetweenRadiuses(heightMap, resolution / 4, resolution / 2 - 1);
  const middleHeightAverage = _averageHeightBetweenRadiuses(heightMap, 0, resolution / 4);

  let radialSlope;
  let point;

  const { minX, maxX, minY, maxY } = binGeometry.binWallBoundingBox();
  const maxBinWallDiameter = Math.max(maxX - minX, maxY - minY);
  const bodyRadius = maxBinWallDiameter / 2;

  if (middleHeightAverage === null && outerRingHeightAverage === null) {
    // We have no pixel data, just use flat 0s for bias function
    radialSlope = 0;
    point = { r: 0, height: 0 };
  } else if (middleHeightAverage !== null && outerRingHeightAverage === null) {
    // bias function toward a heap
    radialSlope = heapAngleOfRepose;
    point = { r: 0 /* centre */, height: middleHeightAverage };
  } else if (outerRingHeightAverage !== null && middleHeightAverage === null) {
    // bias function toward flat surface
    radialSlope = 0;
    point = { r: 0, height: outerRingHeightAverage };
  } else if (outerRingHeightAverage > middleHeightAverage) {
    // bias function toward funnel
    radialSlope = -funnelAngleOfRepose;
    point = { r: resolution / 4 /* between outer ring and middle section */, height: (outerRingHeightAverage + middleHeightAverage) / 2 };
  } else if (outerRingHeightAverage <= middleHeightAverage) {
    // bias function toward heap
    radialSlope = heapAngleOfRepose;
    point = { r: resolution / 4 /* between outer ring and middle section */, height: (outerRingHeightAverage + middleHeightAverage) / 2 };
  }

  // eslint-disable-next-line no-unused-vars
  return function ([r, theta]) {
    const rScaled /* m */ = r * bodyRadius / (resolution / 2);
    const pointRScaled = point.r * bodyRadius / (resolution / 2);
    const slope = -Math.tan(degreesToRadians(radialSlope));

    return slope * (rScaled - pointRScaled) + point.height;
  };
}

function _closeToEmpty(heightMap, binGeometry, emptyThreshold = 0.35 /* m */) {
  const resolution = resolutionOf(heightMap);
  const feedToBinHeights = [];

  for (let x = 0; x < resolution; x++) {
    for (let y = 0; y < resolution; y++) {
      if (heightMap[x][y] !== null) {
        const heightAboveBin = heightMap[x][y] - binGeometry.feedHeightConstraint(x, y, resolution).min;
        feedToBinHeights.push(Math.max(heightAboveBin, 0));
      }
    }
  }

  if (feedToBinHeights.length === 0) {
    // No heights -- assume bin is empty
    return true;
  }

  return _(feedToBinHeights).mean() < emptyThreshold;
}

function _biasTowardEmptyBin(resolution, binGeometry) {
  return function ([r, theta]) {
    const [x, y] = polarToXY([r, theta], resolution);
    return binGeometry.feedHeightConstraint(x, y, resolution).min;
  };
}

function _makeBiasFunction(heightMap, binGeometry) {
  const resolution = resolutionOf(heightMap);

  if (_closeToEmpty(heightMap, binGeometry)) {
    return _biasTowardEmptyBin(resolution, binGeometry);
  }
  return _biasTowardsAFeedPileOrCone(heightMap, resolution, binGeometry);
}

/**
 * @param {[[number|null]]} heightMap
 * @param {number} r1
 * @param {number} r2
 * @return {number|null}
 */
function _averageHeightBetweenRadiuses(heightMap, r1, r2) {
  let average = 0;
  let count = 0;

  const resolution = resolutionOf(heightMap);

  for (let x = 0; x < resolution; x++) {
    for (let y = 0; y < resolution; y++) {
      if (heightMap[x][y] !== null) {
        const [r] = xyToPolar([x, y], resolution);
        if (r >= r1 && r <= r2) {
          count++;
          average += (heightMap[x][y] - average) / count;
        }
      }
    }
  }

  if (count > 0) {
    return average;
  }
  return null;
}
